/**
 * AlertCard — a compact, NON-streaming alert entry for the docked panel's lower
 * "Alert preview" list. Unlike AlertTileStack's live tiles this NEVER mounts a video player:
 * it is the alert's saved snapshot down the card's left side, stretched to the full height of
 * the row, beside three lines — what tripped, where and how long ago, and the model's
 * description of it — with Acknowledge in the top-right corner. This is what remains after a
 * live preview's close timer elapses, and what the page shows for every active alert on reload.
 *
 * READ TOP-DOWN AS AN OPERATOR TRIAGES. The alert's own name leads, in the largest type on
 * the card, because someone working a queue is deciding WHAT happened before they care which
 * camera saw it; the camera and the age follow; the description is last because it elaborates
 * on a line already read. Ack sits opposite the name rather than under the text, so the eye
 * finishes the line it triages on already at the control that acts on it. Severity is a left
 * bar and a dot beside the name rather than a filled band — down a list of these, solid colour
 * on every card stops distinguishing any of them.
 *
 * ACKNOWLEDGE ONLY. Resolving an alert takes a note and a judgement this panel can't carry,
 * so it lives on the alert record — which the card itself opens.
 *
 * The card is EXACTLY the same height in all three media states (snapshot resolved, still
 * loading, none at all) and with or without a description, which is what keeps the list from
 * reflowing as lazily-fetched previews arrive.
 *
 * Preview sourcing is two-tier and NEVER blocks the card from rendering (so the realtime
 * alert feed is never held up by image loading):
 *   1. `alert.snapshotUrl` — a directly-loadable URL (local node) → shown immediately.
 *   2. `loadPreview(alert)` — a lazy resolver (cloud blob-fetches the node's saved image
 *      through the tunnel) → the card renders at once with a pulsing icon, then swaps in
 *      the image when the promise settles. Missing/failed previews keep the icon.
 *
 * One card per alert, latest-first. The whole card opens the alert view (falling back to
 * selecting the camera's hex) — there is no separate Open control, because a second affordance
 * for the same click, repeated down a scrolling list, only competes with Ack.
 */
import { useEffect, useState } from 'react';
import { CheckIcon, PhotoIcon, VideoCameraSlashIcon } from '@heroicons/react/24/outline';
import type { HexAlert } from '../../api/contracts';

/**
 * Severity as a left bar and a dot, on the panel's own white surface — the same move the live
 * tile makes, and for the same reason: a list of solid-coloured blocks is a list in which no
 * single alert stands out. The dot sits against the alert's name so the two are read together.
 */
const SEV: Record<string, { bar: string; dot: string; border: string }> = {
  critical: { bar: 'bg-red-500', dot: 'bg-red-500', border: 'border-border' },
  warning: { bar: 'bg-amber-400', dot: 'bg-amber-400', border: 'border-border' },
  normal: { bar: 'bg-blue-500', dot: 'bg-blue-500', border: 'border-border' },
};

/**
 * The snapshot's WIDTH. Its height is whatever the card's padded box is: the picture stretches
 * to fill the row rather than sitting as a fixed-size thumbnail in it, so it takes every pixel
 * the text column beside it makes available, while a small inset keeps it a rounded object ON
 * the card instead of a panel welded to its corner.
 *
 * Only the width is fixed, and it is fixed rather than proportional so every card in the list
 * is the same shape whether or not an image resolved — the list never reflows when a
 * lazily-fetched preview lands.
 */
const THUMB_W = 'w-[6.5rem]';

function fmtClock(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function timeAgo(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export interface AlertCardProps {
  alert: HexAlert;
  name: string;
  onSelect?: (cameraId: string) => void;
  onAcknowledge?: (alert: HexAlert) => void;
  onViewAlert?: (alert: HexAlert) => void;
  /** Lazy preview resolver for alerts whose snapshot isn't a directly-loadable URL (cloud
   *  blob-fetches the node's saved image through the tunnel). Returns an object/blob URL or
   *  null. The card owns revoking any object URL it receives. */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
}

export default function AlertCard({ alert, name, onSelect, onAcknowledge, onViewAlert, loadPreview }: AlertCardProps) {
  const sev = SEV[alert.severity || 'normal'] || SEV.normal;
  // Whole-card click opens the alert view page; fall back to selecting the hex.
  const openAlert = onViewAlert ? () => onViewAlert(alert) : onSelect ? () => onSelect(alert.cameraId) : undefined;
  const alertName = alert.alertName || alert.eventType || 'Alert';

  const directUrl = alert.snapshotUrl || null;
  // 0 until the node says the snapshot is stored. Alerts now arrive with no image (the node
  // broadcasts before its storage lane has written the event row), so an unresolved card is
  // the normal first state, not a failure — it just shows the live tile until this bumps.
  const previewReady = alert.previewReady || 0;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);
  // Reserve the media box up front when a lazy preview will be attempted, so swapping in the
  // image (or collapsing on miss) doesn't jolt the list. A direct URL needs no fetch.
  const [loadingPreview, setLoadingPreview] = useState<boolean>(() => !directUrl && !!loadPreview);

  // Lazily resolve the saved snapshot WITHOUT blocking the card's initial render. Re-runs per
  // alert identity AND whenever a preview lands for an alert already on screen; revokes any
  // object URL it created on unmount / alert change.
  useEffect(() => {
    setImgError(false);
    setResolvedUrl(null);
    if (directUrl || !loadPreview) {
      setLoadingPreview(false);
      return;
    }
    let cancelled = false;
    let made: string | null = null;
    setLoadingPreview(true);
    loadPreview(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setResolvedUrl(url);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, directUrl, loadPreview, previewReady]);

  const previewSrc = imgError ? null : directUrl || resolvedUrl;

  // Held in every state so all cards are exactly the same height and the list doesn't jump as
  // previews land. A NO-BREAK space where there is no description — a plain one would collapse
  // in HTML and those cards would sit a line shorter than the rest.
  const description = alert.description || ' ';

  return (
    <div data-tour="alert-board-card" className={`group relative overflow-hidden rounded-lg border ${sev.border} bg-card shadow-card transition-shadow hover:shadow-card-hover`}>
      {/* The whole card opens the alert. A full-bleed button BEHIND the content rather than a
          wrapper around it, because the content holds a button of its own (Ack), and a button
          inside a button is invalid HTML that browsers resolve by dropping one of them. The
          content layer is pointer-transparent so clicks fall through to this; Ack opts back in.

          No visible "Open" control: the card IS the control, and a second affordance for the
          same click, repeated down a scrolling list, only competes with the one action on the
          card that isn't the click — Ack. */}
      {openAlert && (
        <button
          type="button"
          aria-label={`Open ${alertName}`}
          title="Open alert"
          className="absolute inset-0 z-0"
          onClick={openAlert}
        />
      )}

      {/* items-stretch (the flex default) is what gives the bar and the picture the row's full
          height — both are siblings of the text column, so the text is what sets the height and
          the other two simply fill it. The severity bar is IN the flow rather than absolutely
          positioned for the same reason: overlaid, it would sit on top of the picture.

          The bar runs edge to edge; everything inside the padded box does not. The picture
          still takes every pixel of height that box has — it is inset from the card, not
          shrunk within it — which is what lets it be a rounded object sitting ON the card
          rather than a panel welded to its corner. */}
      <div className="pointer-events-none relative z-10 flex">
        <span className={`w-1 shrink-0 ${sev.bar}`} />

        <div className="flex min-w-0 flex-1 gap-2.5 p-2">
          <span className={`relative ${THUMB_W} shrink-0 self-stretch overflow-hidden rounded-md bg-slate-900`}>
            {previewSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={previewSrc}
                alt={name}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="absolute inset-0 flex items-center justify-center">
                {/* Pulse in place while the saved snapshot is blob-fetched through the tunnel —
                    the card itself already rendered. */}
                {loadingPreview ? (
                  <PhotoIcon className="h-6 w-6 animate-pulse text-slate-500" />
                ) : (
                  <VideoCameraSlashIcon className="h-6 w-6 text-slate-600" />
                )}
              </span>
            )}
            {alert.timestamp != null && (
              <span className="absolute bottom-0.5 left-1 rounded bg-black/70 px-1 font-mono text-[9px] tabular-nums text-white">
                {fmtClock(alert.timestamp)}
              </span>
            )}
          </span>

          <div className="min-w-0 flex-1">
            {/* WHAT tripped, first and largest, with the one action on the card opposite it. An
                operator working a queue triages by what happened and reaches for the camera only
                once they know what they are looking at — so the name leads, and Ack sits where
                the eye finishes that line rather than at the bottom of a block of text. */}
            <span className="flex items-start gap-2">
              <span className="mt-0.5 flex min-w-0 flex-1 items-center gap-1.5">
                <span className={`h-2 w-2 shrink-0 rounded-full ${sev.dot}`} />
                <span className="min-w-0 truncate text-[13px] font-semibold text-foreground" title={alertName}>
                  {alertName}
                </span>
                {/* Nobody acknowledged it in time: the site's escalation has told level N. */}
                {(alert.escalationLevel || 0) > 1 && (
                  <span
                    className={`shrink-0 rounded border px-1 text-[10px] font-semibold ${alert.escalationState === 'ended' ? 'border-destructive text-destructive' : 'border-warning text-warning'}`}
                    title={alert.escalationState === 'ended'
                      ? `Escalated to level ${alert.escalationLevel} — every level was told and nobody acknowledged`
                      : `Not acknowledged in time — escalated to level ${alert.escalationLevel}`}
                  >
                    Escalated · L{alert.escalationLevel}
                  </span>
                )}
                {/* Repeats inside the rule's cooldown, counted here instead of raised again. */}
                {(alert.occurrences || 0) > 1 && (
                  <span className="shrink-0 rounded border border-border px-1 text-[10px] font-semibold text-muted-foreground" title={`Seen ${alert.occurrences} times — repeats are counted on this alert, not raised again`}>
                    ×{alert.occurrences}
                  </span>
                )}
              </span>
              {/* Acknowledge only. Resolving takes a note and a judgement this panel can't carry —
                  that lives on the alert record, which the card itself opens. */}
              {onAcknowledge && (
                <button
                  type="button"
                  data-tour="alert-board-ack"
                  disabled={!alert.id}
                  onClick={() => onAcknowledge(alert)}
                  className="pointer-events-auto flex shrink-0 items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-950/70"
                >
                  <CheckIcon className="h-3.5 w-3.5" /> Ack
                </button>
              )}
            </span>

            <span className="mt-1 block truncate text-[11px] text-muted-foreground">
              {name}
              {alert.timestamp != null && <> · {timeAgo(alert.timestamp)}</>}
            </span>

            {/* The model's own sentence about what it saw. */}
            <span
              className="mt-0.5 block truncate text-[11px] text-muted-foreground/80"
              title={alert.description || undefined}
            >
              {description}
            </span>
        </div>
        </div>
      </div>
    </div>
  );
}
